/* eslint-disable react/no-unescaped-entities, react-hooks/immutability */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import usePersistedTab from '../../hooks/usePersistedTab';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '../../components/ui/dialog';
import {
    FileText, Download, Send, ArrowLeft, User, Home, Euro,
    Calendar, MapPin, Phone, Mail, CheckCircle, Clock, AlertTriangle,
    FileCheck, Loader2, History, TrendingDown, CalendarPlus,
    Activity, Map, FileSignature, Info, Brain
} from 'lucide-react';
import CreateVisitModal from '../../components/calendar/CreateVisitModal';
import { VisitReportsPanel } from '../../components/visits/VisitReportsPanel';
import { MandateTimelineFull } from '../../components/mandates/MandateTimeline';
import DVFAnalyzer from '../../components/dvf/DVFAnalyzer';
import CompromisSection from '../../components/compromis/CompromisSection';
import QuickOfferSection from '../../components/offers/QuickOfferSection';
import PrepareCallButton from '../../components/mandates/PrepareCallButton';
import TermTooltip from '../../components/help/TermTooltip';
import AICoachingCard from '../../components/mandates/AICoachingCard';
import FollowUpScoreGauge from '../../components/engagement/FollowUpScoreGauge';
import { triggerReinforcement } from '../../components/engagement/reinforcement';

const STATUS_CONFIG = {
    draft: { label: 'Brouillon', color: 'bg-slate-500/20 text-slate-400', icon: FileText },
    generated: { label: 'PDF généré', color: 'bg-blue-500/20 text-blue-400', icon: FileCheck },
    sent: { label: 'Envoyé', color: 'bg-amber-500/20 text-amber-400', icon: Send },
    acknowledged: { label: 'Validé', color: 'bg-green-500/20 text-green-400', icon: CheckCircle }
};

const MANDATE_TYPE_LABELS = {
    simple: 'Simple',
    exclusif: 'Exclusif',
    semi_exclusif: 'Semi-Exclusif'
};

function InfoRow({ label, value, icon: Icon }) {
    if (!value) return null;
    return (
        <div className="flex items-start gap-2 py-1.5">
            {Icon && <Icon className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />}
            <div>
                <p className="text-xs text-muted-foreground">{label}</p>
                <p className="text-sm text-white">{value}</p>
            </div>
        </div>
    );
}

function Panel({ title, children, icon: Icon }) {
    return (
        <div className="rounded-xl border border-border bg-card overflow-hidden">
            <div className="px-4 py-2.5 border-b border-border bg-card flex items-center gap-2">
                {Icon && <Icon className="w-3.5 h-3.5 text-[#FF914D]" />}
                <h3 className="font-semibold text-white text-xs uppercase tracking-wider">{title}</h3>
            </div>
            <div className="p-4">{children}</div>
        </div>
    );
}

export default function MandateDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [mandate, setMandate] = useState(null);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [sending, setSending] = useState(false);
    const [amendments, setAmendments] = useState([]);
    const [showAmendmentDialog, setShowAmendmentDialog] = useState(false);
    const [newPrice, setNewPrice] = useState('');
    const [amendmentReason, setAmendmentReason] = useState('');
    const [creatingAmendment, setCreatingAmendment] = useState(false);
    const [activeTab, setActiveTab] = usePersistedTab(
        'homly_mandate_detail_tab',
        'apercu',
        ['apercu', 'coaching', 'activity', 'marche', 'docs'],
    );
    const [showVisitModal, setShowVisitModal] = useState(false);
    const [visitModalMode, setVisitModalMode] = useState('future');
    const [gaugeRefresh, setGaugeRefresh] = useState(0);

    useEffect(() => {
        loadMandate();
    }, [id]);

    useEffect(() => {
        if (mandate) {
            loadAmendments();
        }
    }, [mandate]);

    const loadMandate = async () => {
        try {
            const res = await api.get(`/mandates-v2/${id}`);
            setMandate(res.data);
        } catch (err) {
            console.error('Error loading mandate:', err);
            toast.error('Mandat non trouvé');
            navigate('/agent/mandates');
        } finally {
            setLoading(false);
        }
    };

    const loadAmendments = async () => {
        try {
            const res = await api.get(`/mandates-v2/${id}/amendments`);
            setAmendments(res.data.amendments || []);
        } catch (err) {
            console.error('Error loading amendments:', err);
        }
    };

    const handleGeneratePDF = async () => {
        setGenerating(true);
        try {
            const res = await api.post(`/mandates-v2/${id}/generate-pdf`);
            toast.success(`PDF v${res.data.version} généré avec succès`);
            loadMandate();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur génération PDF');
        } finally {
            setGenerating(false);
        }
    };

    const handleDownloadPDF = async (version) => {
        try {
            const url = version
                ? `/mandates-v2/${id}/download-pdf?version=${version}`
                : `/mandates-v2/${id}/download-pdf`;

            const res = await api.get(url, { responseType: 'blob' });
            const blob = new Blob([res.data], { type: 'application/pdf' });
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = `Mandat_${mandate?.mandate_number || id}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);
        } catch (err) {
            toast.error('Erreur téléchargement PDF');
        }
    };

    const handleSend = async () => {
        if (!mandate?.versions?.length) {
            toast.error('Veuillez d\'abord générer le PDF');
            return;
        }

        setSending(true);
        try {
            const res = await api.post(`/mandates-v2/${id}/send`);
            toast.success(`Mandat envoyé à ${res.data.sent_to?.length || 0} destinataire(s)`);
            loadMandate();
            triggerReinforcement('mandate_updated', id, () => setGaugeRefresh((k) => k + 1));
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur envoi');
        } finally {
            setSending(false);
        }
    };

    const handleCreateAmendment = async () => {
        const price = parseFloat(newPrice);
        if (isNaN(price) || price <= 0) {
            toast.error('Veuillez entrer un prix valide');
            return;
        }

        const currentPrice = mandate?.pricing?.sale_price || 0;
        if (price >= currentPrice) {
            toast.error('Le nouveau prix doit être inférieur au prix actuel');
            return;
        }

        setCreatingAmendment(true);
        try {
            const res = await api.post(`/mandates-v2/${id}/amendments`, null, {
                params: { new_price: price, reason: amendmentReason || undefined }
            });
            toast.success(`Avenant créé : -${res.data.reduction_percent}%`);
            setShowAmendmentDialog(false);
            setNewPrice('');
            setAmendmentReason('');
            loadAmendments();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur création avenant');
        } finally {
            setCreatingAmendment(false);
        }
    };

    const handleSendAmendment = async (amendmentId) => {
        try {
            await api.post(`/mandates-v2/${id}/amendments/${amendmentId}/send`);
            toast.success('Avenant envoyé au propriétaire');
            loadAmendments();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur envoi avenant');
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-background p-6">
                <div className="max-w-5xl mx-auto space-y-4">
                    <Skeleton className="h-8 w-64 bg-muted" />
                    <Skeleton className="h-64 w-full bg-muted" />
                </div>
            </div>
        );
    }

    if (!mandate) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <p className="text-muted-foreground">Mandat non trouvé</p>
            </div>
        );
    }

    const statusConfig = STATUS_CONFIG[mandate.status] || STATUS_CONFIG.draft;
    const StatusIcon = statusConfig.icon;
    const salePrice = mandate.pricing?.sale_price || 0;
    const commissionRate = mandate.pricing?.commission_rate || 0;
    const commissionAmount = salePrice * commissionRate / 100;
    const basePath = user?.role === 'admin' ? '/admin' : '/agent';

    return (
        <div className="min-h-screen bg-background text-white p-3 sm:p-6">
            <div className="max-w-5xl mx-auto">
                {/* Header compact */}
                <div className="mb-4">
                    <Link
                        to={`${basePath}/mandates`}
                        className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-white mb-3 py-1 px-2 -ml-2 rounded-lg hover:bg-card transition-colors"
                        data-testid="back-to-mandates"
                    >
                        <ArrowLeft className="w-3.5 h-3.5" />
                        <span>Retour aux mandats</span>
                    </Link>

                    <div className="flex flex-wrap items-center gap-2 mb-2">
                        <h1 className="text-xl sm:text-2xl font-black text-white">
                            Mandat {mandate.mandate_number}
                        </h1>
                        <Badge className={statusConfig.color}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {statusConfig.label}
                        </Badge>
                        <Badge className="bg-[#681793]/20 text-[#681793] border-0">
                            <TermTooltip
                                term={
                                    mandate.mandate_type === 'exclusif' ? 'mandat_exclusif' :
                                    mandate.mandate_type === 'semi_exclusif' ? 'mandat_semi_exclusif' :
                                    'mandat_simple'
                                }
                                className="!no-underline"
                            >
                                {MANDATE_TYPE_LABELS[mandate.mandate_type] || 'Simple'}
                            </TermTooltip>
                        </Badge>
                    </div>
                </div>

                {/* Lifecycle Timeline (always visible — gives context) */}
                <Card className="bg-card border-border mb-4">
                    <CardContent className="p-3 sm:p-4">
                        <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-2">Avancement du mandat</p>
                        <MandateTimelineFull status={mandate.status} />
                    </CardContent>
                </Card>

                {/* M2/M7 — Score de suivi vendeur (jauge bienveillante, monte après chaque action) */}
                {user?.role !== 'seller' && (
                    <FollowUpScoreGauge mandateId={mandate.id} refreshKey={gaugeRefresh} />
                )}

                {/* Action buttons (always visible — desktop) */}
                <Card className="bg-card border-border mb-4 hidden md:block">
                    <CardContent className="p-3">
                        <div className="flex flex-wrap items-center gap-2">
                            {mandate.versions?.length > 0 ? (
                                <>
                                    <Button
                                        onClick={handleSend}
                                        disabled={sending || mandate.status === 'sent' || mandate.status === 'acknowledged'}
                                        size="sm"
                                        className="bg-gradient-to-r from-[#681793] to-[#FF914D] hover:opacity-90 shadow-lg shadow-[#681793]/30"
                                        data-testid="send-mandate-btn"
                                    >
                                        {sending ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
                                        {mandate.status === 'sent' || mandate.status === 'acknowledged' ? 'Déjà envoyé' : 'Envoyer pour signature'}
                                    </Button>
                                    <Button
                                        onClick={() => { setVisitModalMode('future'); setShowVisitModal(true); }}
                                        size="sm"
                                        variant="outline"
                                        className="border-purple-500/30 text-purple-400 hover:bg-purple-500/10"
                                        data-testid="plan-visit-btn"
                                    >
                                        <CalendarPlus className="w-3.5 h-3.5 mr-1.5" />
                                        Planifier visite
                                    </Button>
                                    <Button
                                        onClick={() => { setVisitModalMode('past'); setShowVisitModal(true); }}
                                        size="sm"
                                        variant="outline"
                                        className="border-green-500/30 text-green-400 hover:bg-green-500/10"
                                        data-testid="log-past-visit-btn"
                                    >
                                        <CheckCircle className="w-3.5 h-3.5 mr-1.5" />
                                        Logger visite
                                    </Button>
                                    <Button
                                        onClick={() => handleDownloadPDF()}
                                        size="sm"
                                        variant="outline"
                                        className="border-border text-muted-foreground hover:text-white"
                                    >
                                        <Download className="w-3.5 h-3.5 mr-1.5" />
                                        PDF
                                    </Button>
                                    <Button
                                        onClick={handleGeneratePDF}
                                        disabled={generating}
                                        size="sm"
                                        variant="ghost"
                                        className="text-muted-foreground hover:text-white text-xs"
                                    >
                                        {generating ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <FileText className="w-3.5 h-3.5 mr-1.5" />}
                                        Régénérer v{mandate.current_version + 1}
                                    </Button>
                                </>
                            ) : (
                                <Button
                                    onClick={handleGeneratePDF}
                                    disabled={generating}
                                    className="bg-gradient-to-r from-[#681793] to-[#FF914D] hover:opacity-90 shadow-lg shadow-[#681793]/30"
                                    data-testid="generate-pdf-btn"
                                >
                                    {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
                                    Générer le mandat PDF
                                </Button>
                            )}
                        </div>
                    </CardContent>
                </Card>

                {/* Validation banner */}
                {mandate.status === 'acknowledged' && (
                    <Card className="bg-green-500/10 border-green-500/20 mb-4">
                        <CardContent className="p-3">
                            <div className="flex items-start gap-2">
                                <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                                <div className="text-sm">
                                    <p className="text-green-400 font-medium">Mandat validé par le vendeur</p>
                                    <p className="text-xs text-green-300/70">
                                        Validé le {new Date(mandate.acknowledged_at).toLocaleString('fr-FR')}
                                    </p>
                                    {mandate.acknowledgment_comment && (
                                        <p className="text-xs text-green-300/50 mt-1 italic">"{mandate.acknowledgment_comment}"</p>
                                    )}
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Tabs : Aperçu / Activité / Marché / Documents */}
                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                    <TabsList className="w-full grid grid-cols-5 h-auto bg-card border border-border p-1 rounded-xl" data-testid="mandate-tabs">
                        <TabsTrigger value="apercu" className="flex items-center gap-1.5 py-2 data-[state=active]:bg-[#681793]/20 data-[state=active]:text-white rounded-lg" data-testid="tab-apercu">
                            <Info className="w-3.5 h-3.5" />
                            <span className="text-xs md:text-sm">Aperçu</span>
                        </TabsTrigger>
                        <TabsTrigger value="coaching" className="flex items-center gap-1.5 py-2 data-[state=active]:bg-[#A855F7]/20 data-[state=active]:text-white rounded-lg" data-testid="tab-coaching">
                            <Brain className="w-3.5 h-3.5" />
                            <span className="text-xs md:text-sm">Coaching IA</span>
                        </TabsTrigger>
                        <TabsTrigger value="activity" className="flex items-center gap-1.5 py-2 data-[state=active]:bg-[#681793]/20 data-[state=active]:text-white rounded-lg" data-testid="tab-activity">
                            <Activity className="w-3.5 h-3.5" />
                            <span className="text-xs md:text-sm">Activité</span>
                        </TabsTrigger>
                        <TabsTrigger value="marche" className="flex items-center gap-1.5 py-2 data-[state=active]:bg-[#681793]/20 data-[state=active]:text-white rounded-lg" data-testid="tab-marche">
                            <Map className="w-3.5 h-3.5" />
                            <span className="text-xs md:text-sm">Marché</span>
                        </TabsTrigger>
                        <TabsTrigger value="docs" className="flex items-center gap-1.5 py-2 data-[state=active]:bg-[#681793]/20 data-[state=active]:text-white rounded-lg" data-testid="tab-docs">
                            <FileSignature className="w-3.5 h-3.5" />
                            <span className="text-xs md:text-sm">Docs</span>
                            {(mandate.versions?.length > 0 || amendments.length > 0) && (
                                <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full bg-[#FF914D]/20 text-[#FF914D] font-bold">
                                    {(mandate.versions?.length || 0) + amendments.length}
                                </span>
                            )}
                        </TabsTrigger>
                    </TabsList>

                    {/* Aperçu : Vendeurs / Bien / Prix / Durée */}
                    <TabsContent value="apercu" className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Panel title="Vendeur(s)" icon={User}>
                            {mandate.sellers?.map((seller, idx) => (
                                <div key={idx} className="mb-3 last:mb-0 pb-3 last:pb-0 border-b last:border-0 border-border">
                                    <p className="font-medium text-white flex items-center gap-2">
                                        <User className="w-4 h-4 text-[#FF914D]" />
                                        {seller.civility === 'M' ? 'M.' : 'Mme'} {seller.first_name} {seller.last_name}
                                    </p>
                                    <div className="ml-6 space-y-1 mt-1.5">
                                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                                            <Mail className="w-3 h-3" /> {seller.email}
                                        </p>
                                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                                            <Phone className="w-3 h-3" /> {seller.phone}
                                        </p>
                                        {seller.ownership_type === 'indivision' && (
                                            <Badge variant="outline" className="text-xs border-white/20">
                                                Indivision {seller.ownership_share}%
                                            </Badge>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </Panel>

                        <Panel title="Bien immobilier" icon={Home}>
                            <div className="flex items-start gap-2 mb-2.5">
                                <Home className="w-4 h-4 text-[#FF914D] mt-0.5" />
                                <div>
                                    <p className="text-sm text-white capitalize">
                                        {mandate.property?.property_type?.replace('_', ' ')}
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                        {mandate.property?.surface_habitable} m² • {mandate.property?.rooms} pièces
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-start gap-2">
                                <MapPin className="w-4 h-4 text-muted-foreground mt-0.5" />
                                <p className="text-sm text-muted-foreground">
                                    {mandate.property?.address}<br />
                                    {mandate.property?.postal_code} {mandate.property?.city}
                                </p>
                            </div>
                        </Panel>

                        <Panel title="Prix et honoraires" icon={Euro}>
                            <div className="space-y-2">
                                <div>
                                    <p className="text-xs text-muted-foreground">Prix de vente</p>
                                    <p className="text-2xl font-bold text-white">
                                        {salePrice.toLocaleString('fr-FR')} €
                                    </p>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-sm text-muted-foreground">Honoraires ({commissionRate}%)</span>
                                    <span className="text-[#FF914D] font-medium">
                                        {commissionAmount.toLocaleString('fr-FR')} €
                                    </span>
                                </div>
                                <div className="text-xs text-muted-foreground">
                                    À la charge {mandate.pricing?.commission_payer === 'vendeur' ? 'du vendeur' : "de l'acquéreur"}
                                </div>
                            </div>
                        </Panel>

                        <Panel title="Durée" icon={Calendar}>
                            <InfoRow
                                label="Date de début"
                                value={mandate.duration?.start_date ? new Date(mandate.duration.start_date).toLocaleDateString('fr-FR') : '-'}
                                icon={Calendar}
                            />
                            <InfoRow
                                label="Durée initiale"
                                value={`${mandate.duration?.initial_months || 3} mois`}
                                icon={Clock}
                            />
                            {mandate.duration?.auto_renewal && (
                                <p className="text-xs text-muted-foreground mt-2">
                                    Tacite reconduction par périodes de {mandate.duration?.renewal_period_months || 12} mois
                                </p>
                            )}
                        </Panel>
                    </TabsContent>

                    {/* Coaching IA — Sprint 5 (Coach · IA Homly) */}
                    <TabsContent value="coaching" className="mt-4">
                        <AICoachingCard mandateId={id} />
                    </TabsContent>

                    {/* Activité : commerciale / offres / compromis */}
                    <TabsContent value="activity" className="mt-4 space-y-4">
                        <Panel title="Activité commerciale" icon={Activity}>
                            <div className="mb-4 pb-4 border-b border-slate-200 flex items-center justify-between gap-3 flex-wrap">
                                <div>
                                    <p className="text-sm font-semibold text-slate-900">Préparer un point avec le vendeur</p>
                                    <p className="text-xs text-slate-500">Le Coach analyse le mandat et génère un script personnalisé en 3 secondes.</p>
                                </div>
                                <PrepareCallButton
                                    mandateId={mandate.id}
                                    sellerName={mandate.sellers?.[0]?.first_name ? `${mandate.sellers[0].first_name} ${mandate.sellers[0].last_name || ''}`.trim() : 'Vendeur'}
                                />
                            </div>
                        </Panel>

                        <Panel title="Offres reçues" icon={Euro}>
                            <QuickOfferSection
                                mandateId={mandate.id}
                                askingPrice={mandate?.pricing?.sale_price || salePrice}
                            />
                        </Panel>

                        <Panel title={(<><TermTooltip term="compromis">Compromis</TermTooltip> / <TermTooltip term="acte_authentique">Acte authentique</TermTooltip></>)} icon={FileSignature}>
                            <CompromisSection mandateId={mandate.id} />
                        </Panel>

                        <Panel title="Comptes rendus de visite" icon={CheckCircle}>
                            <VisitReportsPanel
                                mandateId={mandate.id}
                                propertyAddress={`${mandate.property?.address || ''} ${mandate.property?.postal_code || ''} ${mandate.property?.city || ''}`}
                                isDark={true}
                            />
                        </Panel>
                    </TabsContent>

                    {/* Marché : DVF */}
                    <TabsContent value="marche" className="mt-4">
                        <Panel title={(<><TermTooltip term="dvf">DVF</TermTooltip> — Marché immobilier (base officielle gov)</>)} icon={Map}>
                            {mandate.property?.address ? (
                                <DVFAnalyzer
                                    address={mandate.property.address}
                                    postalCode={mandate.property.postal_code}
                                    propertyType={(() => {
                                        const t = String(mandate.property?.property_type || '').toLowerCase();
                                        if (t.includes('appart')) return 'apartment';
                                        if (t.includes('maison') || t.includes('house')) return 'house';
                                        if (t.includes('terrain') || t.includes('land')) return 'land';
                                        return 'apartment';
                                    })()}
                                    surfaceM2={mandate.property?.surface_habitable || mandate.property?.surface}
                                    rooms={mandate.property?.rooms}
                                    askingPrice={salePrice}
                                />
                            ) : (
                                <p className="text-xs text-muted-foreground">Adresse du bien manquante — ajoutez l'adresse pour activer l'analyse DVF.</p>
                            )}
                        </Panel>
                    </TabsContent>

                    {/* Documents : Versions PDF / Avenants / Historique */}
                    <TabsContent value="docs" className="mt-4 space-y-4">
                        {/* PDF uploadé (workflow Upload + IA) */}
                        {mandate.documents?.uploaded_pdf_filename && (
                            <Panel title="PDF du mandat (uploadé)" icon={FileText}>
                                <div className="flex items-center justify-between gap-3 flex-wrap">
                                    <div className="flex items-center gap-3 min-w-0">
                                        <div className="w-10 h-10 rounded-lg bg-orange-500/10 border border-orange-500/30 flex items-center justify-center shrink-0">
                                            <FileText className="w-5 h-5 text-orange-400" />
                                        </div>
                                        <div className="min-w-0">
                                            <div className="font-medium truncate">{mandate.documents.uploaded_pdf_filename}</div>
                                            <div className="text-xs text-muted-foreground">Document source signé sur CRM externe</div>
                                        </div>
                                    </div>
                                    <Button
                                        size="sm"
                                        onClick={async () => {
                                            try {
                                                const res = await api.get(`/mandate-upload/${mandate.id}/pdf`, { responseType: 'blob' });
                                                const url = URL.createObjectURL(res.data);
                                                const a = document.createElement('a');
                                                a.href = url;
                                                a.download = mandate.documents.uploaded_pdf_filename || `mandat-${mandate.mandate_number}.pdf`;
                                                document.body.appendChild(a);
                                                a.click();
                                                a.remove();
                                                URL.revokeObjectURL(url);
                                            } catch (e) {
                                                toast.error(e.response?.data?.detail || 'Téléchargement impossible');
                                            }
                                        }}
                                        className="bg-orange-500 hover:bg-orange-600"
                                        data-testid="download-uploaded-pdf-btn"
                                    >
                                        <Download className="w-4 h-4 mr-2" />Télécharger
                                    </Button>
                                </div>
                            </Panel>
                        )}

                        {/* Versions PDF */}
                        {mandate.versions?.length > 0 && (
                            <Panel title="Versions du PDF" icon={History}>
                                <div className="flex flex-wrap gap-2">
                                    {mandate.versions.map((v) => (
                                        <button
                                            key={v.version}
                                            onClick={() => handleDownloadPDF(v.version)}
                                            className="text-xs px-3 py-1.5 rounded-lg bg-card border border-border text-muted-foreground hover:bg-muted hover:text-white flex items-center gap-1.5 transition-colors"
                                        >
                                            <Download className="w-3 h-3" />
                                            v{v.version}
                                            <span className="text-white/30">
                                                ({new Date(v.generated_at).toLocaleDateString('fr-FR')})
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            </Panel>
                        )}

                        {/* Avenants & historique des prix */}
                        <Panel title="Historique des prix et avenants" icon={TrendingDown}>
                            <div className="flex items-center justify-between mb-3">
                                <p className="text-sm text-muted-foreground">
                                    {amendments.length === 0
                                        ? 'Aucune modification de prix'
                                        : `${amendments.length} avenant(s) de prix`}
                                </p>
                                <Dialog open={showAmendmentDialog} onOpenChange={setShowAmendmentDialog}>
                                    <DialogTrigger asChild>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            className="border-[#FF914D]/30 text-[#FF914D] hover:bg-[#FF914D]/10"
                                        >
                                            <TrendingDown className="w-4 h-4 mr-2" />
                                            Proposer une baisse
                                        </Button>
                                    </DialogTrigger>
                                    <DialogContent className="bg-slate-900 border-border text-white">
                                        <DialogHeader>
                                            <DialogTitle>Proposer une baisse de prix</DialogTitle>
                                            <DialogDescription className="text-muted-foreground">
                                                Cette modification devra être validée par le propriétaire.
                                            </DialogDescription>
                                        </DialogHeader>
                                        <div className="space-y-4 py-4">
                                            <div>
                                                <p className="text-sm text-muted-foreground mb-2">Prix actuel</p>
                                                <p className="text-2xl font-bold text-white">
                                                    {salePrice.toLocaleString('fr-FR')} €
                                                </p>
                                            </div>
                                            <div>
                                                <label className="text-sm text-muted-foreground mb-2 block">
                                                    Nouveau prix proposé
                                                </label>
                                                <Input
                                                    type="number"
                                                    value={newPrice}
                                                    onChange={(e) => setNewPrice(e.target.value)}
                                                    placeholder="Ex: 450000"
                                                    className="bg-card border-border text-white"
                                                />
                                                {newPrice && parseFloat(newPrice) < salePrice && (
                                                    <p className="text-sm text-green-400 mt-2">
                                                        Réduction de {((1 - parseFloat(newPrice) / salePrice) * 100).toFixed(1)}%
                                                        {' '}soit -{(salePrice - parseFloat(newPrice)).toLocaleString('fr-FR')} €
                                                    </p>
                                                )}
                                            </div>
                                            <div>
                                                <label className="text-sm text-muted-foreground mb-2 block">
                                                    Justification (optionnel)
                                                </label>
                                                <Textarea
                                                    value={amendmentReason}
                                                    onChange={(e) => setAmendmentReason(e.target.value)}
                                                    placeholder="Ex: Ajustement suite aux retours du marché..."
                                                    className="bg-card border-border text-white resize-none"
                                                    rows={3}
                                                />
                                            </div>
                                        </div>
                                        <DialogFooter>
                                            <Button
                                                variant="outline"
                                                onClick={() => setShowAmendmentDialog(false)}
                                                className="border-border text-muted-foreground"
                                            >
                                                Annuler
                                            </Button>
                                            <Button
                                                onClick={handleCreateAmendment}
                                                disabled={creatingAmendment || !newPrice || parseFloat(newPrice) >= salePrice}
                                                className="bg-gradient-to-r from-[#681793] to-[#FF914D]"
                                            >
                                                {creatingAmendment && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                                Créer l'avenant
                                            </Button>
                                        </DialogFooter>
                                    </DialogContent>
                                </Dialog>
                            </div>

                            {amendments.length > 0 && (
                                <div className="space-y-2">
                                    {amendments.map((amendment, idx) => (
                                        <div
                                            key={idx}
                                            className={`p-3 rounded-lg border ${
                                                amendment.status === 'validated'
                                                    ? 'bg-green-500/10 border-green-500/20'
                                                    : amendment.status === 'sent'
                                                    ? 'bg-amber-500/10 border-amber-500/20'
                                                    : 'bg-card border-border'
                                            }`}
                                        >
                                            <div className="flex items-start justify-between gap-3">
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                                                        <TrendingDown className="w-4 h-4 text-red-400" />
                                                        <span className="text-white font-medium text-sm">
                                                            {amendment.previous_price?.toLocaleString('fr-FR')} € → {amendment.new_price?.toLocaleString('fr-FR')} €
                                                        </span>
                                                        <Badge className={`${
                                                            amendment.status === 'validated' ? 'bg-green-500/20 text-green-300' :
                                                            amendment.status === 'sent' ? 'bg-amber-500/20 text-amber-300' :
                                                            amendment.status === 'rejected' ? 'bg-red-500/20 text-red-300' :
                                                            'bg-slate-500/20 text-slate-300'
                                                        } border-0 text-xs`}>
                                                            {amendment.status === 'validated' ? 'Validé' :
                                                             amendment.status === 'sent' ? 'Envoyé' :
                                                             amendment.status === 'rejected' ? 'Refusé' :
                                                             'En attente'}
                                                        </Badge>
                                                    </div>
                                                    <p className="text-xs text-muted-foreground">
                                                        -{amendment.reduction_percent?.toFixed(1)}% ({amendment.reduction_amount?.toLocaleString('fr-FR')} €)
                                                    </p>
                                                    {amendment.reason && (
                                                        <p className="text-xs text-muted-foreground italic mt-1">"{amendment.reason}"</p>
                                                    )}
                                                    <p className="text-[10px] text-white/30 mt-1">
                                                        Créé le {new Date(amendment.created_at).toLocaleString('fr-FR')}
                                                        {amendment.validated_at && (
                                                            <span className="text-green-400/70">
                                                                {' '}• Validé le {new Date(amendment.validated_at).toLocaleString('fr-FR')}
                                                            </span>
                                                        )}
                                                    </p>
                                                </div>
                                                {amendment.status === 'pending' && (
                                                    <Button
                                                        size="sm"
                                                        onClick={() => handleSendAmendment(amendment.id)}
                                                        className="bg-gradient-to-r from-[#681793] to-[#FF914D] flex-shrink-0"
                                                    >
                                                        <Send className="w-3 h-3 mr-1" />
                                                        Envoyer
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {mandate.price_history?.length > 0 && (
                                <div className="mt-3 pt-3 border-t border-border">
                                    <p className="text-xs text-muted-foreground mb-1.5">Historique des modifications</p>
                                    {mandate.price_history.map((history, idx) => (
                                        <div key={idx} className="text-xs text-muted-foreground py-1">
                                            {new Date(history.validated_at).toLocaleDateString('fr-FR')} :
                                            {' '}{history.previous_price?.toLocaleString('fr-FR')} € → {history.new_price?.toLocaleString('fr-FR')} €
                                        </div>
                                    ))}
                                </div>
                            )}
                        </Panel>
                    </TabsContent>
                </Tabs>

                {/* Timestamps footer */}
                <div className="mt-6 mb-24 md:mb-0 text-[10px] text-white/30 flex flex-wrap gap-3">
                    <span>Créé le {new Date(mandate.created_at).toLocaleString('fr-FR')}</span>
                    {mandate.generated_at && (
                        <span>PDF généré le {new Date(mandate.generated_at).toLocaleString('fr-FR')}</span>
                    )}
                    {mandate.sent_at && (
                        <span>Envoyé le {new Date(mandate.sent_at).toLocaleString('fr-FR')}</span>
                    )}
                </div>
            </div>

            {/* Visit Modal */}
            <CreateVisitModal
                isOpen={showVisitModal}
                onClose={() => setShowVisitModal(false)}
                mandateId={mandate?.id}
                propertyAddress={mandate?.property?.address}
                initialMode={visitModalMode}
                onSuccess={() => {
                    toast.success(visitModalMode === 'past' ? 'Visite enregistrée !' : 'Visite planifiée !');
                    triggerReinforcement('visit_logged', id, () => setGaugeRefresh((k) => k + 1));
                }}
            />

            {/* Mobile Floating Action Bar */}
            <div className="md:hidden fixed bottom-16 left-0 right-0 z-[60] px-3 pb-2" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 4.5rem)' }}>
                <div className="bg-card/95 backdrop-blur-xl border border-border rounded-2xl p-3 shadow-2xl shadow-black/40 flex items-center gap-2" data-testid="mobile-action-bar">
                    {mandate.versions?.length > 0 ? (
                        <>
                            <Button
                                onClick={handleSend}
                                disabled={sending || mandate.status === 'sent' || mandate.status === 'acknowledged'}
                                className="flex-1 bg-gradient-to-r from-[#681793] to-[#FF914D] hover:opacity-90 text-white h-10 text-xs font-semibold"
                                data-testid="mobile-send-btn"
                            >
                                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}
                                {mandate.status === 'sent' || mandate.status === 'acknowledged' ? 'Envoyé' : 'Envoyer'}
                            </Button>
                            <Button
                                onClick={() => handleDownloadPDF()}
                                variant="outline"
                                className="border-border text-muted-foreground h-10 px-3"
                                data-testid="mobile-download-btn"
                            >
                                <Download className="w-4 h-4" />
                            </Button>
                            <Button
                                onClick={() => setShowVisitModal(true)}
                                variant="outline"
                                className="border-purple-500/30 text-purple-400 h-10 px-3"
                                data-testid="mobile-visit-btn"
                            >
                                <CalendarPlus className="w-4 h-4" />
                            </Button>
                        </>
                    ) : (
                        <Button
                            onClick={handleGeneratePDF}
                            disabled={generating}
                            className="flex-1 bg-gradient-to-r from-[#681793] to-[#FF914D] hover:opacity-90 text-white h-10 text-sm font-semibold"
                            data-testid="mobile-generate-btn"
                        >
                            {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
                            Générer le PDF
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
}
